import { useState } from "react";
import { EuiCallOut, EuiConfirmModal } from "@inscopix/ideas-eui";
import { File as DrsFile } from "graphql/_Types";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import assert from "assert";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { getDrsFileModifyPermissionByDrsFileAndAction } from "types/DrsFileModifyPermissions";
import { evictCacheFragment } from "utils/cache-fragments";
import { useSelectedDrsFileId } from "stores/useSelectedDrsFileId";
import { useFileUploadContext } from "stores/upload/FileUploadProvider";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { isDefined } from "utils/isDefined";

export type ModalDeleteDrsFileProps = {
  drsFiles: Pick<
    DrsFile,
    | "id"
    | "status"
    | "name"
    | "source"
    | "isSeries"
    | "projectId"
    | "seriesParentId"
    | "processingStatus"
  >[];
  onClose: () => void;
  onComplete?: ({
    successfulFiles,
    errors,
  }: {
    successfulFiles: Pick<DrsFile, "id">[];
    errors: Error[];
  }) => void;
};

export const ModalDeleteDrsFile = ({
  drsFiles,
  onClose,
  onComplete,
}: ModalDeleteDrsFileProps) => {
  const deselectDrsFile = useSelectedDrsFileId((s) => s.deselectDrsFile);
  const cancelFileUploads = useFileUploadContext((s) => s.cancelFileUploads);
  const deleteFile = useDataTableContext((s) => s.deleteFile);

  const deletableFiles = drsFiles.filter(
    (drsFile) =>
      getDrsFileModifyPermissionByDrsFileAndAction(drsFile, "DELETE")
        .isPermitted,
  );

  assert(
    deletableFiles.length > 0,
    "Expected at least one deletable drsFile in ModalDeleteDrsFile",
  );

  const [isLoading, setIsLoading] = useState(false);

  const hardDeleteFiles = async (drsFiles: Pick<DrsFile, "id">[]) => {
    const hardDeleteDrsFilesFromCache = (drsFiles: Pick<DrsFile, "id">[]) => {
      const fileIds = drsFiles.map((file) => file.id);
      cancelFileUploads(fileIds);

      drsFiles.forEach((drsFile) =>
        evictCacheFragment({ __typename: "File", id: drsFile.id }),
      );
    };

    const requests = drsFiles.map(async ({ id }) => {
      try {
        const { data, error } = await deleteFile({
          fileId: id,
          mode: "delete",
        });
        return error ?? data;
      } catch (err) {
        return new Error(`Failed to delete drsFile ${id}`);
      }
    });

    const drsFilesDeletedResults = await Promise.all(requests);

    const successfullyDeletedFiles = drsFilesDeletedResults.filter(
      function (
        objectOrError,
      ): objectOrError is NonNullable<
        Awaited<ReturnType<typeof deleteFile>>["data"]
      > {
        return !(objectOrError instanceof Error);
      },
    );

    const errors = drsFilesDeletedResults.filter(
      function (result): result is Error {
        return result instanceof Error;
      },
    );

    if (errors.length > 0) {
      addUtilityToastFailure(
        `Failed to delete ${errors.length} out of ${drsFilesDeletedResults.length} files.`,
      );
    } else {
      addUtilityToastSuccess(`Successfully deleted ${requests.length} files.`);
    }

    hardDeleteDrsFilesFromCache(successfullyDeletedFiles);
    if (isDefined(onComplete)) {
      onComplete({ successfulFiles: successfullyDeletedFiles, errors });
    }
    return { successfulFiles: successfullyDeletedFiles };
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const hardDeletedFiles = await hardDeleteFiles(deletableFiles);
    hardDeletedFiles.successfulFiles.forEach((file) =>
      deselectDrsFile(file.id),
    );
    setIsLoading(false);
    onClose();
  };

  return (
    <EuiConfirmModal
      title="Delete File"
      cancelButtonText="Cancel"
      confirmButtonText="Confirm"
      onCancel={onClose}
      onConfirm={() => void handleSubmit()}
      buttonColor="danger"
      defaultFocusedButton="cancel"
      isLoading={isLoading}
    >
      <EuiCallOut
        title="This will permanently remove this file from all data tables and analysis
        tables"
        color="warning"
        iconType="warning"
      >
        This action cannot be undone. Consider archiving the file if you want to
        free up active storage space without permanently deleting it.
      </EuiCallOut>
    </EuiConfirmModal>
  );
};
